/**
 * Beauty Loft Spa — Hero Banner Slider Controller
 * Clean, lightweight, dependency-free vanilla JS carousel with touch & theme-editor support.
 */

class BLHeroSlider {
  constructor(container) {
    this.container = container;
    this.slides = Array.from(this.container.querySelectorAll('.bl-hero-slider__slide'));
    this.dots = Array.from(this.container.querySelectorAll('.bl-hero-slider__dot'));
    this.prevBtn = this.container.querySelector('.bl-hero-slider__arrow--prev');
    this.nextBtn = this.container.querySelector('.bl-hero-slider__arrow--next');

    if (!this.slides.length) return;

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autoplay = this.container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.container.dataset.autoplaySpeed, 10) || 7000;
    this.timer = null;
    this.isTransitioning = false;
    this.initialImageReady = false;
    this.abortController = new AbortController();
    this.destroyed = false;
    this.preloadTimeout = null;

    // Touch variables
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.init();
  }

  init() {
    this.goToSlide(0);

    // Keep the first slide on screen for its full interval after its photo is ready.
    // A slow connection must not rotate away from the image before it can paint.
    const initialImage = this.slides[0].querySelector('img');
    const startWhenReady = async () => {
      if (initialImage?.decode) {
        await initialImage.decode().catch(() => {});
      }
      if (this.destroyed) return;
      this.initialImageReady = true;
      this.startAutoplay();
    };
    if (!initialImage || initialImage.complete) {
      void startWhenReady();
    } else {
      initialImage.addEventListener('load', startWhenReady, { once: true, signal: this.abortController.signal });
      initialImage.addEventListener('error', startWhenReady, { once: true, signal: this.abortController.signal });
    }

    if (this.dots.length) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(index);
          this.restartAutoplay();
        }, { signal: this.abortController.signal });
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prevSlide();
        this.restartAutoplay();
      }, { signal: this.abortController.signal });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.nextSlide();
        this.restartAutoplay();
      }, { signal: this.abortController.signal });
    }

    // Pause on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoplay(), { signal: this.abortController.signal });
    this.container.addEventListener('mouseleave', () => this.startAutoplay(), { signal: this.abortController.signal });

    // Touch events
    this.container.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true, signal: this.abortController.signal });

    this.container.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true, signal: this.abortController.signal });

    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
        this.restartAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
        this.restartAutoplay();
      }
    }, { signal: this.abortController.signal });

    this.startAutoplay();
  }

  loadSlideMedia(slide) {
    if (this.destroyed) return;
    const template = slide.querySelector('template[data-bl-slide-media]');
    if (template) {
      template.replaceWith(template.content.cloneNode(true));
      const image = slide.querySelector('img');
      if (image) image.loading = 'eager';
    }
  }

  async goToSlide(index) {
    if (index < 0) {
      index = this.totalSlides - 1;
    } else if (index >= this.totalSlides) {
      index = 0;
    }

    const request = this.navigationRequest = (this.navigationRequest || 0) + 1;
    this.loadSlideMedia(this.slides[index]);
    const image = this.slides[index].querySelector('img');
    if (index !== this.currentIndex && image && !image.complete) {
      await image.decode().catch(() => {});
      if (this.destroyed || request !== this.navigationRequest) return;
    }

    if (index !== this.currentIndex) {
      this.slides[this.currentIndex].classList.remove('is-initial');
    }

    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add('is-active');
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      }
    });

    this.currentIndex = index;
  }

  nextSlide() {
    this.goToSlide(this.currentIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentIndex - 1);
  }

  startAutoplay() {
    const isHovered = window.matchMedia('(hover: hover)').matches && this.container.matches(':hover');
    if (this.destroyed || !this.autoplay || this.totalSlides <= 1 || !this.initialImageReady || isHovered) return;
    this.stopAutoplay();
    // Warm only the next photo shortly before it is needed, leaving startup bandwidth for the visible slide.
    this.preloadTimeout = setTimeout(() => {
      this.loadSlideMedia(this.slides[(this.currentIndex + 1) % this.totalSlides]);
    }, Math.max(0, this.autoplaySpeed - 2000));
    const timer = setTimeout(() => {
      void this.goToSlide(this.currentIndex + 1).then(() => {
        if (!this.destroyed && this.timer === timer) this.startAutoplay();
      });
    }, this.autoplaySpeed);
    this.timer = timer;
  }

  stopAutoplay() {
    clearTimeout(this.preloadTimeout);
    this.preloadTimeout = null;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  handleSwipe() {
    const diff = this.touchEndX - this.touchStartX;
    if (Math.abs(diff) > 45) {
      if (diff < 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
      this.restartAutoplay();
    }
  }

  destroy() {
    this.destroyed = true;
    this.navigationRequest = (this.navigationRequest || 0) + 1;
    this.stopAutoplay();
    this.abortController?.abort();
    this.container._blHeroSliderInitialized = false;
    this.container._blHeroSliderInstance = null;
  }
}

function initBLHeroSliders() {
  document.querySelectorAll('[data-bl-hero-slider]').forEach((slider) => {
    if (!slider._blHeroSliderInitialized) {
      slider._blHeroSliderInstance = new BLHeroSlider(slider);
      slider._blHeroSliderInitialized = true;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBLHeroSliders);
} else {
  initBLHeroSliders();
}

// Shopify Theme Editor Event Handlers
document.addEventListener('shopify:section:load', (e) => {
  const slider = e.target.querySelector('[data-bl-hero-slider]');
  if (slider) {
    slider._blHeroSliderInstance?.destroy();
    slider._blHeroSliderInstance = new BLHeroSlider(slider);
    slider._blHeroSliderInitialized = true;
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-bl-hero-slider]').forEach((slider) => {
    slider._blHeroSliderInstance?.destroy();
  });
});

document.addEventListener('shopify:block:select', (e) => {
  const block = e.target;
  const slider = block.closest('[data-bl-hero-slider]');
  if (slider && block.classList.contains('bl-hero-slider__slide')) {
    const slides = Array.from(slider.querySelectorAll('.bl-hero-slider__slide'));
    const index = slides.indexOf(block);
    if (index !== -1 && slider._blHeroSliderInstance) {
      slider._blHeroSliderInstance.goToSlide(index);
      slider._blHeroSliderInstance.stopAutoplay();
    }
  }
});
